import { AfterViewInit, Component, ElementRef, Input, OnInit, Output, ViewChild, EventEmitter } from '@angular/core';
import { SiteParameter, SiteTwin } from 'src/api/models';
import { FireForesightService } from 'src/api/services';

@Component({
  selector: 'image-zone-editor',
  templateUrl: './image-zone-editor.component.html',
  styleUrls: ['./image-zone-editor.component.css']
})
export class ImageZoneEditorComponent implements OnInit {

  //@Input() shown: boolean = false;
  @Input() siteTwin: SiteTwin;
  //@Input() selectedImage: SiteParameter;
  //@Output() onClosed = new EventEmitter();

  selection: any;
  imageSrc: string;

  parent: any;
  canvas: any;
  ctx: any;
  scale: number;
  panX: number;
  panY: number;
  zeroPosition: number;
  imagePaths: string[];

  images = [];
  imagesLoaded = 0;

  constructor(private svc: FireForesightService) {
  }

  ngOnInit(): void {
    this.loadCanvas();
    window.addEventListener('resize', () => this.resizeCanvas());
    this.resizeCanvas();
  }

  loadCanvas() {
    this.canvas = document.getElementById('panoramaCanvas');
    this.parent = document.getElementById('panoramaParent');
    
    this.ctx = (this.canvas as any).getContext('2d');

    this.panX = 0;
    this.panY = 0;
    this.scale = 1;

    let isPanning = false;
    let startPan = { x: 0, y: 0 };
    let startMouse = { x: 0, y: 0 };

    const minScale = 0.1;
    const maxScale = 2;

    this.imagePaths = [];
    
    const params = Object.values(this.siteTwin.parameters);
    const objects = params
        .filter(a => !a.hidden && a.bearing >= 0 && a.latestValue)
        .sort((a, b) => a.bearing - b.bearing);

    for (let p of objects) {
      this.imagePaths.push(p.latestImageUrl);
    }

    this.zeroPosition = objects[0].bearing - (objects[0].fieldOfView/2); // Adjust as needed

    this.loadImages(() => this.init());

    this.canvas.addEventListener('mousedown', (e) => {
      isPanning = true;
      startMouse = { x: e.clientX, y: e.clientY };
      startPan = { x: this.panX, y: this.panY };
      this.canvas.style.cursor = 'grabbing';
    });

    this.canvas.addEventListener('mousemove', (e) => {
      if (!isPanning) return;

      let dx = e.clientX - startMouse.x;
      this.panX = startPan.x + dx;

      if (this.canPanVertically()) {
        let dy = e.clientY - startMouse.y;
        this.panY = startPan.y + dy;
        this.constrainPan();
      } else {
        this.centerImageVertically();
      }

      this.draw();
    });

    this.canvas.addEventListener('mouseup', () => {
      isPanning = false;
      this.canvas.style.cursor = 'grab';
    });

    this.canvas.addEventListener('mouseleave', () => {
      isPanning = false;
      this.canvas.style.cursor = 'grab';
    });

    this.canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      let zoomFactor = 1.1;
      let mouseX = e.clientX - this.canvas.offsetLeft;
      let mouseY = e.clientY - this.canvas.offsetTop;

      let zoom = e.deltaY < 0 ? zoomFactor : 1 / zoomFactor;
      let newScale = this.scale * zoom;

      if (newScale < minScale || newScale > maxScale) return;

      this.panX = mouseX - ((mouseX - this.panX) * (newScale / this.scale));

      if (this.canPanVertically()) {
        this.panY = mouseY - ((mouseY - this.panY) * (newScale / this.scale));
        this.constrainPan();
      } else {
        this.scale = newScale; // Update scale before centering
        this.centerImageVertically();
      }

      this.scale = newScale;

      this.draw();
    });
  }

  loadImages(callback) {
    this.imagePaths.forEach((path, index) => {
      const img = new Image();
      img.src = path;
      img.onload = () => {
        this.images[index] = img;
        this.imagesLoaded++;
        if (this.imagesLoaded === this.imagePaths.length) {
          callback();
        }
      };
      img.onerror = () => {
        console.error(`Failed to load image at path: ${path}`);
      };
    });
  }

  init() {
    this.centerImageVertically();
    this.canvas.style.cursor = 'grab';
    this.draw();
  }

  resizeCanvas() {
    this.canvas.width = parent.innerWidth;
    this.canvas.height = parent.innerHeight;
    this.draw();
  }


  draw() {
    if (this.images.length === 0) return;

    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.ctx.imageSmoothingEnabled = true;

    const totalWidth = this.images.reduce((sum, img) => sum + img.width, 0) * this.scale;
    const imgHeight = this.images[0].height * this.scale;

    let offsetX = this.panX % totalWidth;
    if (offsetX > 0) offsetX -= totalWidth;

    const repeatCount = Math.ceil(this.canvas.width / totalWidth) + 2;

    for (let i = -1; i < repeatCount; i++) {
      let xPosition = offsetX + i * totalWidth;
      let currentX = xPosition;

      this.images.forEach((img) => {
        let imgWidth = img.width * this.scale;
        this.ctx.drawImage(
          img,
          currentX,
          this.panY,
          imgWidth,
          imgHeight
        );
        currentX += imgWidth;
      });
    }

    this.drawCompass();
  }

  drawCompass() {
    let baseInterval = 10; // Base interval for headings
    if (this.scale > 1.5) {
        baseInterval = 4;
    } else if (this.scale > 1) {
        baseInterval = 5;
    } else {
        baseInterval = 10;
    }

    const totalWidth = this.images.reduce((sum, img) => sum + img.width, 0) * this.scale;

    // Adjust the interval based on zoom level
    let interval = baseInterval / Math.max(1, this.scale);

    // Ensure interval is rounded to a sensible value
    interval = interval > 15 ? 30 : interval > 10 ? 15 : interval > 5 ? 10 : interval > 2.5 ? 5 : 1;

    // Calculate the initial heading that is closest to zero position
    let startHeading = Math.ceil(this.zeroPosition / interval) * interval;

    for (let i = 0; i <= 360 / interval; i++) {
        // Calculate the heading
        let heading = (startHeading + i * interval) % 360;

        let xPos = ((heading - this.zeroPosition) / 360 * totalWidth + this.panX) % totalWidth;
        if (xPos < 0) xPos += totalWidth;

        // Skip lines outside the visible area
        if (xPos < -50 || xPos > this.canvas.width + 50) continue;

        // Draw the vertical line
        this.ctx.beginPath();
        this.ctx.moveTo(xPos, 0);
        this.ctx.lineTo(xPos, this.canvas.height); // Line extends the full height of the canvas
        this.ctx.strokeStyle = 'rgba(255, 0, 0, 0.2)';
        this.ctx.lineWidth = 2;
        this.ctx.stroke();

        // Draw the heading label
        this.ctx.font = `20px Arial`;
        this.ctx.fillStyle = `rgba(255, 255, 255, 1.0)`; // Fully opaque text
        this.ctx.textAlign = 'center';
        this.ctx.fillText(`${heading}°`, xPos, 30);
    }
  }

  canPanVertically() {
    const imgHeight = this.images[0].height * this.scale;
    return imgHeight > this.canvas.height;
  }

  centerImageVertically() {
    const imgHeight = this.images[0].height * this.scale;
    this.panY = (this.canvas.height - imgHeight) / 2;
  }

  constrainPan() {
    const imgHeight = this.images[0].height * this.scale;
    const maxPanY = 0;
    const minPanY = this.canvas.height - imgHeight;
    this.panY = Math.max(minPanY, Math.min(maxPanY, this.panY));
  }


  setZeroPosition(degrees) {
    this.zeroPosition = degrees % 360;
    this.draw();
  }

}
